import React, { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { EcointelLottie } from './EcoIntelLoader';
import StagingBanner from './StagingBanner';
import { emailMatchesStagingAllowlist, getAppEnvLabel, isInviteOnlyBuild } from '../utils/stagingAccess';

/**
 * Staging/dev gate: after Firebase Auth, verify the signed-in email is on the
 * invite allowlist (systemConfig/environment in Firestore). Prod builds skip this.
 */
export default function StagingAccessGate() {
  const [gate, setGate] = useState(() => (isInviteOnlyBuild() ? 'loading' : 'ok'));

  useEffect(() => {
    if (!isInviteOnlyBuild()) return undefined;

    let cancelled = false;
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        if (!cancelled) setGate('ok');
        return;
      }

      if (!cancelled) setGate('loading');

      try {
        const snap = await getDoc(doc(db, 'systemConfig', 'environment'));
        const config = snap.exists() ? snap.data() : null;
        const allowed = emailMatchesStagingAllowlist(user.email, config);
        if (!cancelled) setGate(allowed ? 'ok' : 'denied');
      } catch (err) {
        console.error('StagingAccessGate: could not load environment config', err);
        if (!cancelled) setGate('denied');
      }
    });

    return () => {
      cancelled = true;
      unsub();
    };
  }, []);

  if (!isInviteOnlyBuild()) {
    return <Outlet />;
  }

  const envLabel = getAppEnvLabel() === 'development' ? 'Development' : 'Staging';

  if (gate === 'loading') {
    return (
      <div className="min-h-screen flex flex-col bg-slate-50">
        <StagingBanner />
        <div className="flex-1 flex items-center justify-center">
          <EcointelLottie message={`Checking ${envLabel.toLowerCase()} access...`} lottiePixelWidth={140} lottiePixelHeight={175} />
        </div>
      </div>
    );
  }

  if (gate === 'denied') {
    return (
      <div className="min-h-screen flex flex-col bg-slate-50">
        <StagingBanner />
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center">
            <h1 className="text-xl font-bold text-slate-900 mb-2">{envLabel} is invite-only</h1>
            <p className="text-slate-600 text-sm mb-6">
              Your account is not on the {envLabel.toLowerCase()} allowlist. Ask an EcoIntel admin to add your email
              if you need access for testing.
            </p>
            <button
              type="button"
              onClick={() => signOut(auth)}
              className="px-5 py-2.5 bg-[#043132] text-white font-medium rounded-xl hover:bg-[#065354] transition-colors"
            >
              Sign out
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <StagingBanner />
      <Outlet />
    </>
  );
}
